import React, { useState, useContext, useCallback } from "react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import "./LogoUpload.css";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import Axios from "src/network/Axios";

const maxDataSize = 1000000;
const dataTypes = ["image/jpeg", "image/png"];

export const LogoUpload = () => {
    const { t } = useTranslation();
    const { authStore } = useContext(RootStoreContext);

    const [fileBase64, setFileBase64] = useState<string | undefined>(undefined);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const user = authStore.user;

    const handleChangeInput = useCallback(
        async (event: any) => {
            const toBase64 = (file: Blob) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = (e) => {
                        //only use second part of encoding!
                        resolve(reader.result?.toString().split(",")[1]);
                    };
                    reader.onerror = (error) => reject(error);
                });
            const newFile = event.target.files[0];
            if (newFile.size > maxDataSize) {
                setWarningMessage(t("error.wrong_size", { size: (maxDataSize / 1000000).toString() }));
                return;
            }
            if (dataTypes.indexOf(newFile.type) === -1) {
                setWarningMessage(t("error.wrong_imagetype"));
                return;
            }
            await toBase64(newFile).then((f) => {
                setWarningMessage(null);
                setFileBase64(f as string);
            });
        },
        [setFileBase64, setWarningMessage, t]
    );

    const onUpload = useCallback(async () => {
        setIsUploading(true);
        if (fileBase64 && user) {
            try {
                await Axios.getAuthInstance().put(NetworkConfig.brandAssets.uploadLogo, { file: fileBase64 });
                window.location.reload();
            } catch (error: any) {
                setWarningMessage(t("error.generic") + " - " + error);
            } finally {
                setIsUploading(false);
            }
        } else {
            setIsUploading(true);
        }

        return () => {
            setIsUploading(false);
        };
    }, [setIsUploading, setWarningMessage, fileBase64, user, t]);

    const imageSrc = fileBase64 ? "data:image/jpeg;base64," + fileBase64 : `${NetworkConfig.brandAssets.logoImage}`;

    return (
        <div className="LogoUpload d-flex flex-column">
            <div className="LogoContainer flex-fill">
                <img src={imageSrc} alt="Customer Logo" />
            </div>
            <input className="mt-2 mb-4" type="file" onChange={handleChangeInput} accept={dataTypes.join(",")} />
            <UI.Button
                label={isUploading ? t("labels.uploading") : t("labels.upload")}
                onClick={onUpload}
                disabled={(fileBase64 ? false : true) || isUploading}
            />
            {warningMessage && (
                <UI.Alert variant="warning" className="mt-3">
                    {warningMessage}
                </UI.Alert>
            )}
        </div>
    );
};
